import axios, { AxiosError, AxiosInstance } from "axios";
import {
  IInvoiceEntry,
  IOrderDetails,
  IShippingPackageSize,
  IShippingRates,
  IShippingWeight,
  IStripePrice,
  IStripeProduct,
} from "../models";
import { IUserBasicDetails } from "../models/userInfo";
import { IAddress } from "../models/address";
import { IAppState } from "../types/states";
import base64ToBlob from "../util/base64ToBlob";
import { errorLogger } from "./errorLogger";

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface ICheckoutRequest {
  /** Customer ID */
  customerID: string;
  /** Invoice entries */
  invoiceList: IInvoiceEntry[];
  /** User email */
  email: string;
  /** Order ID */
  orderID: string;
}

export interface ICheckoutSessionData {
  id: string;
}

export interface IFinalizeOrderResponse {
  id: string;
  finalized: boolean;
}

export interface IProductPriceRequest {
  /** App product code */
  appProductCode: string;
  /** App product sub code */
  appProductSubCode?: string;
  /** Quantity */
  quantity: number;
}

export interface IProductPriceResponse {
  product: IStripeProduct;
  price: IStripePrice;
}

export interface IShippingRatesRequest {
  toCity: string;
  toState: string;
  toCountry: string;
  toPostalCode: string;
  quantity: number;
}

export interface IShippingRatesResponse {
  packageWeight: IShippingWeight;
  packageSize: IShippingPackageSize;
  rates: IShippingRates[];
}

export interface ISaveHeadshotOptionsRequest {
  pdfData: Omit<IAppState, 'imageBase64File' | 'imageForCanvas' | 'PDFURL' | 'finalPDFBlob'>;
  pdfID?: string;
  uploadedFileID?: string;
  userID?: string;
}

export class BwhsApiError extends Error {
  code: string;
  data?: Record<string, unknown>;

  constructor(code: string, message: string, options: ErrorOptions = {}, data?: Record<string, unknown>) {
    super(message, options);
    this.name = "BwhsApiError";
    this.code = code;
    this.data = data;
  }
}

export default class BwhsApiClient {
  backendBaseUrl: string | undefined;
  axiosInstance: AxiosInstance;

  constructor() {
    this.backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    this.axiosInstance = axios.create({
      baseURL: this.backendBaseUrl,
      timeout: 120000,
    });
  }

  private shouldRetryRequest(error: AxiosError) {
    if (!error.response) {
      return true;
    }
    return error.response.status >= 500 || error.response.status === 429;
  }

  private async runWithRetry<T>(
    operation: () => Promise<T>,
    retryCount = 2,
    initialBackoffMs = 500,
  ) {
    let attempt = 0;
    let backoff = initialBackoffMs;

    while (true) {
      try {
        return await operation();
      } catch (error) {
        const isAxiosError = error instanceof AxiosError;
        if (
          attempt >= retryCount ||
          !isAxiosError ||
          !this.shouldRetryRequest(error)
        ) {
          throw error;
        }
        await sleep(backoff);
        backoff *= 2;
        attempt += 1;
      }
    }
  }

  async getShippingRates(shippingRatesRequest: IShippingRatesRequest) {
    try {
      const response = await this.axiosInstance.post<IShippingRatesResponse>(
        "/shipping-rates",
        shippingRatesRequest,
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError(
          "shipping_failed",
          "Failed to get shipping rates",
        );
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorCode = error.response?.data?.error || "shipping_failed";
        const errorMessage = error.response?.data?.message || error.message;
        const bwhsError = new BwhsApiError(errorCode, errorMessage);
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'getShippingRates',
        });
        throw bwhsError;
      }
      if (error instanceof BwhsApiError) {
        throw error;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error)
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'getShippingRates',
      });
      throw unknownError;
    }
  }

  async getProductPrice(
    priceRequest: IProductPriceRequest,
    abortController?: AbortController,
  ) {
    try {
      const response = await this.axiosInstance.post<IProductPriceResponse>(
        "/product-price",
        priceRequest,
        { signal: abortController?.signal },
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError("price_failed", "Failed to get product price");
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      } else if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 404) {
          bwhsError = new BwhsApiError("product_not_found", "Product not found");
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'getProductPrice',
        });
        throw bwhsError;
      } else {
        const unknownError = new BwhsApiError(
          "unknown-error",
          error instanceof Error ? error.message : String(error),
          { cause: error }
        );
        errorLogger.logApiError(unknownError, {
          component: 'BwhsApiClient',
          action: 'getProductPrice',
        });
        throw unknownError;
      }
    }
  }

  async updateOrder(orderDetails: IOrderDetails, id?: string) {
    try {
      const params: Record<string, string> = {};
      if (id) {
        params.id = id;
      }
      const response = await this.axiosInstance.post(`/order`, orderDetails, {
        params,
      });
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError("order_update_failed", "Failed to update order");
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 500) {
          bwhsError = new BwhsApiError(
            "order_update_failed",
            "Failed to update order",
          );
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'updateOrder',
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error }
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'updateOrder',
      });
      throw unknownError;
    }
  }

  async processCheckout(
    checkoutRequest: ICheckoutRequest,
    abortController?: AbortController,
  ) {
    try {
      const response = await this.axiosInstance.post<ICheckoutSessionData>(
        "/payment",
        checkoutRequest,
        { signal: abortController?.signal },
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError("checkout_failed", "Failed to process checkout");
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorCode = error.response?.data?.error || "checkout_failed";
        const errorMessage = error.response?.data?.message || error.message;
        const bwhsError = new BwhsApiError(errorCode, errorMessage, {}, error.response?.data);
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'processCheckout',
        });
        throw bwhsError;
      }
      if (error instanceof BwhsApiError) {
        throw error;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error)
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'processCheckout',
      });
      throw unknownError;
    }
  }

  async createUser(userInfo: IUserBasicDetails, billingAddress: IAddress) {
    try {
      const response = await this.axiosInstance.post<string>("/user/create", {
        userInfo,
        billingAddress,
      });
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError("user_create_failed", "Failed to create user");
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorCode = error.response?.data?.error || "user_create_failed";
        const errorMessage = error.response?.data?.message || error.message;
        const bwhsError = new BwhsApiError(errorCode, errorMessage);
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'createUser',
        });
        throw bwhsError;
      }
      if (error instanceof BwhsApiError) {
        throw error;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error)
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'createUser',
      });
      throw unknownError;
    }
  }

  async createOrder(orderDetails: IOrderDetails, id?: string) {
    try {
      const params: Record<string, string> = {};
      if (id) {
        params.id = id;
      }

      const response = await this.axiosInstance.post<{ id?: string }>(
        "/order",
        orderDetails,
        { params },
      );
      if (response.status === 200) {
        return response.data?.id;
      } else {
        throw new BwhsApiError("order_create_failed", "Failed to create order");
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 500) {
          bwhsError = new BwhsApiError(
            "order_create_failed",
            "Failed to create order",
          );
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'createOrder',
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error }
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'createOrder',
      });
      throw unknownError;
    }
  }

  async saveFinalPdf(pdfBlob: Blob, orderId: string) {
    try {
      const formData1 = new FormData();
      formData1.append("PDF", pdfBlob, `BWAY_HEADSHOTS_${orderId}.pdf`);

      const response = await this.runWithRetry(
        () =>
          this.axiosInstance.post<string>("/pdf", formData1, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError(
          "save_final_pdf_failed",
          "Failed to save final PDF",
        );
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 500) {
          bwhsError = new BwhsApiError(
            "save_final_pdf_failed",
            "Failed to save final PDF",
          );
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'saveFinalPdf',
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error }
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'saveFinalPdf',
      });
      throw unknownError;
    }
  }

  async saveOriginalFile(fileBase64: string, orderId: string) {
    try {
      const formData1 = new FormData();
      formData1.append(
        "original_file",
        base64ToBlob(fileBase64),
        `Uploaded_file_${orderId}`,
      );

      const response = await this.runWithRetry(
        () =>
          this.axiosInstance.post<string>("/uploaded-file", formData1, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError(
          "save_original_file_failed",
          "Failed to save original file",
        );
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 500) {
          bwhsError = new BwhsApiError(
            "save_original_file_failed",
            "Failed to save original file",
          );
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'saveOriginalFile',
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error }
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'saveOriginalFile',
      });
      throw unknownError;
    }
  }

  async saveHeadshotOptions(options: ISaveHeadshotOptionsRequest) {
    try {
      const response = await this.axiosInstance.post(`/pdf-data`, options);
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError(
          "save_headshot_options_failed",
          "Failed to save headshot options",
        );
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        let bwhsError: BwhsApiError;
        if (error.response?.status === 500) {
          bwhsError = new BwhsApiError(
            "save_headshot_options_failed",
            "Failed to save headshot options",
          );
        } else {
          bwhsError = new BwhsApiError("network-error", error.message, {
            cause: error,
          });
        }
        errorLogger.logApiError(bwhsError, {
          component: 'BwhsApiClient',
          action: 'saveHeadshotOptions',
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error }
      );
      errorLogger.logApiError(unknownError, {
        component: 'BwhsApiClient',
        action: 'saveHeadshotOptions',
      });
      throw unknownError;
    }
  }

  async finalizeOrder(orderId: string) {
    try {
      const response = await this.axiosInstance.post<IFinalizeOrderResponse>(
        "/order/finalize",
        { orderId },
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new BwhsApiError("order_finalize_failed", "Failed to finalize order");
      }
    } catch (error) {
      if (error instanceof BwhsApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        const errorCode = error.response?.data?.error || "order_finalize_failed";
        const errorMessage = error.response?.data?.message || error.message;
        const bwhsError = new BwhsApiError(errorCode, errorMessage, {
          cause: error,
        });
        errorLogger.logApiError(bwhsError, {
          component: "BwhsApiClient",
          action: "finalizeOrder",
        });
        throw bwhsError;
      }
      const unknownError = new BwhsApiError(
        "unknown-error",
        error instanceof Error ? error.message : String(error),
        { cause: error },
      );
      errorLogger.logApiError(unknownError, {
        component: "BwhsApiClient",
        action: "finalizeOrder",
      });
      throw unknownError;
    }
  }
}
